import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../Authentication/LanguageContext";
import "./Levels.css";

interface Level {
  key: string;
  german: string;
  color: string;
  gradient: [string, string];
  pattern: string;
}

interface LevelTranslation {
  german: string;
  translation: Record<string, string>;
}

interface Translations {
  a1: LevelTranslation;
  a2: LevelTranslation;
  b1: LevelTranslation;
  b2: LevelTranslation;
  c1: LevelTranslation;
  c2: LevelTranslation;
}

const translations: Translations = {
  a1: {
    german: 'Anfänger',
    translation: {
      en: 'Beginner',
      de: 'Anfänger',
      ta: 'ஆரம்பம்',
      si: 'ආරම්භය',
      hi: 'शुरुआत',
      ar: 'مبتدئ',
      tr: 'Başlangıç',
      ru: 'Новичок',
      zh: '初学',
      fr: 'Début',
      es: 'Inicio',
    },
  },
  a2: {
    german: 'Grundlagen',
    translation: {
      en: 'Elementary',
      de: 'Grundlagen',
      ta: 'அடிப்படை',
      si: 'මූලික',
      hi: 'बुनियादी',
      ar: 'أساسي',
      tr: 'Temel',
      ru: 'Базовый',
      zh: '基础',
      fr: 'Base',
      es: 'Básico',
    },
  },
  b1: {
    german: 'Mittelstufe',
    translation: {
      en: 'Intermediate',
      de: 'Mittelstufe',
      ta: 'நடுத்தரம்',
      si: 'මධ්‍යම',
      hi: 'मध्यम',
      ar: 'متوسط',
      tr: 'Orta',
      ru: 'Средний',
      zh: '中级',
      fr: 'Moyen',
      es: 'Medio',
    },
  },
  b2: {
    german: 'Obere Mittelstufe',
    translation: {
      en: 'Upper Intermediate',
      de: 'Obere Mittelstufe',
      ta: 'மேல்நிலை',
      si: 'උසස් මට්ටම',
      hi: 'उच्च मध्यम',
      ar: 'متوسط أعلى',
      tr: 'Orta Üstü',
      ru: 'Выше среднего',
      zh: '中高',
      fr: 'Intermédiaire supérieur',
      es: 'Intermedio alto',
    },
  },
  c1: {
    german: 'Fortgeschritten',
    translation: {
      en: 'Advanced',
      de: 'Fortgeschritten',
      ta: 'திறமையானது',
      si: 'උසස්',
      hi: 'उन्नत',
      ar: 'متقدم',
      tr: 'İleri',
      ru: 'Продвинутый',
      zh: '高级',
      fr: 'Avancé',
      es: 'Avanzado',
    },
  },
  c2: {
    german: 'Experte',
    translation: {
      en: 'Proficient',
      de: 'Experte',
      ta: 'நிபுணத்துவம்',
      si: 'ප්‍රවීණ',
      hi: 'प्रवीण',
      ar: 'خبير',
      tr: 'Uzman',
      ru: 'Эксперт',
      zh: '精通',
      fr: 'Maître',
      es: 'Experto',
    },
  },
};

const levels: Level[] = [
  { 
    key: "a1", 
    german: "Anfänger", 
    color: "#14B8A6", 
    gradient: ["#5EEAD4", "#2DD4BF"],
    pattern: "diagonal-stripes"
  },
  { 
    key: "a2", 
    german: "Grundlagen", 
    color: "#3B82F6", 
    gradient: ["#93C5FD", "#3B82F6"],
    pattern: "zigzag"
  },
  { 
    key: "b1", 
    german: "Mittelstufe", 
    color: "#EF4444", 
    gradient: ["#FCA5A5", "#EF4444"],
    pattern: "dots"
  },
  { 
    key: "b2", 
    german: "Obere Mittelstufe", 
    color: "#22C55E", 
    gradient: ["#6EE7B7", "#3CB371"],
    pattern: "waves"
  },
  { 
    key: "c1", 
    german: "Fortgeschritten", 
    color: "#8B5CF6", 
    gradient: ["#C084FC", "#8B5CF6"],
    pattern: "grid"
  },
  { 
    key: "c2", 
    german: "Experte", 
    color: "#EAB308", 
    gradient: ["#FDE68A", "#F59E0B"],
    pattern: "crosshatch"
  },
];

const Levels: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const handleClick = (level: string) => {
    if (level === "a1") {
      navigate("/levels/a1");
    } else {
      alert(`Coming soon: ${level}`);
    }
  };

  return (
    <div className="levels-wrapper">
      <div className="levels-grid">
        {levels.map((level) => {
          const translation = translations[level.key as keyof Translations];
          const translatedText = translation.translation[language] || level.german;
          
          return (
            <div
              key={level.key}
              className={`level-card level-card-${level.key} ${level.pattern}`}
              onClick={() => handleClick(level.key)}
              style={{
                background: `linear-gradient(135deg, ${level.gradient[0]}, ${level.gradient[1]})`,
                '--pattern-color': `${level.color}20`
              } as React.CSSProperties}
            >
              <div
                className="level-badge"
                style={{ backgroundColor: level.color }}
              >
                {level.key.toUpperCase()}
              </div>
              <div className="level-info">
                <h2>{level.german}</h2>
                <p>{translatedText}</p>
              </div>
              <div className="level-progress">
                <div className="progress-bar" style={{ width: `${(levels.findIndex(l => l.key === level.key) + 1) * 16.66}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Levels; 